/**
 * Focus by pointer or by keyboard (DESIGN.md › Colors, the Focus Ring Rule; THEME.md §3.5, §11.2; T-243).
 *
 * Safari draws the focus ring when a script moves the focus after a tap or a click: a tap never focuses a button, so
 * nothing tells it the pointer was in use. Horizon's drawers and dialogs move the focus to their close button as
 * they open, and back to the opener as they close, so on an iPhone the ring showed at once on a tap. Chrome doesn't
 * draw it then.
 *
 * This marks a control that takes the focus while the last input was a pointer (a tap, a click, a pen) with
 * data-okt-pointer-focus, and the token layer hides its ring until the next key that moves or acts (Tab, the
 * arrows, Enter, Escape, ...), which clears every mark. Fields are never marked, as they ring on any focus, and a
 * keyboard user never meets a mark: the key that opens a drawer has already switched to the keyboard. Nor is the
 * control a press focuses itself, which no browser rings (T-248).
 */

const MARK = 'data-okt-pointer-focus';

// What rings on any focus (DESIGN.md › Inputs / Fields)
const FIELD =
  'input:not([type="checkbox"], [type="radio"], [type="range"], [type="button"], [type="submit"], [type="reset"], [type="image"], [type="color"], [type="file"]), textarea, select, [contenteditable]:not([contenteditable="false"])';

// Keys that only modify another, which tell nothing about the input in use
const MODIFIERS = new Set(['Shift', 'Control', 'Alt', 'AltGraph', 'Meta', 'CapsLock', 'Fn', 'FnLock', 'OS', 'Hyper', 'Super']);

let pointer = false;

/** @type {EventTarget | null} What the current press landed on, until its task ends */
let pressed = null;

function unmark() {
  for (const element of document.querySelectorAll(`[${MARK}]`)) element.removeAttribute(MARK);
}

// A pointer: pointerdown, and touchstart and mousedown for browsers or tools that send no pointer events for a tap
for (const type of ['pointerdown', 'touchstart', 'mousedown']) {
  addEventListener(
    type,
    (event) => {
      pointer = true;
      pressed = event.target;
      setTimeout(() => {
        pressed = null;
      });
    },
    { capture: true, passive: true }
  );
}

addEventListener(
  'keydown',
  (event) => {
    if (MODIFIERS.has(event.key)) return;
    // Typing a character into a field is not moving through the page
    if (event.key.length === 1 && event.target instanceof Element && event.target.matches(FIELD)) return;
    pointer = false;
    unmark();
  },
  { capture: true }
);

// A control the press itself focuses (Chrome and Firefox focus a button on a click) isn't marked: no browser rings
// that focus, and the mark, which :has() rules read, would restyle the whole page as it comes and goes (T-248)
addEventListener(
  'focusin',
  (event) => {
    const target = event.target;
    if (!pointer || !(target instanceof Element) || target.matches(FIELD)) return;
    if (pressed instanceof Node && target.contains(pressed)) return;
    target.setAttribute(MARK, '');
  },
  { capture: true }
);

addEventListener(
  'focusout',
  (event) => {
    if (event.target instanceof Element) event.target.removeAttribute(MARK);
  },
  { capture: true }
);

// A re-render (Horizon's morph, after an add to cart) copies the server's attributes onto the focused close button
// and drops the mark while the focus stays: put it back, still before the next paint (T-245)
new MutationObserver((records) => {
  if (!pointer) return;
  for (const record of records) {
    const target = record.target;
    if (target === document.activeElement && target instanceof Element && !target.hasAttribute(MARK) && !target.matches(FIELD)) {
      target.setAttribute(MARK, '');
    }
  }
}).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: [MARK] });
